import { useEffect, useState } from "react";
import Button from "@mui/material/Button";

import { selectCurrentAccount } from "fond/api/accountSlice";
import { selectUserId } from "fond/api/userSlice";
import { ACCOUNT_CONTEXT } from "fond/constants";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";
import { AccountSwitchDisplay } from "fond/widgets/AccountSwitch/AccountSwitchDisplay";
import Modal from "fond/widgets/Modal";

/**
 * Modal that appears when the account context becomes stale due to changes in another tab.
 * Gives the user the option to switch to the new account or stay on the current account.
 */
export const StaleAccountModal: React.FC = () => {
  const userId = useAppSelector(selectUserId);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { accounts } = useSwitchableAccounts();

  const [isStale, setIsStale] = useState(false);
  const [targetAccountId, setTargetAccountId] = useState<string | null>(null);

  // Find the target account from switchable accounts
  const targetAccount = targetAccountId ? accounts.find((account) => account.ID === targetAccountId) : null;

  useEffect(() => {
    const showAlert = (e: StorageEvent) => {
      if (e.key === ACCOUNT_CONTEXT && userId) {
        // Parse the new localStorage value
        const newContext = e.newValue ? JSON.parse(e.newValue) : {};
        const newAccountId = newContext[userId];

        // If localStorage now matches Redux, close the modal
        if (newAccountId === currentAccount?.ID) {
          setIsStale(false);
          setTargetAccountId(null);
        } else {
          // Otherwise, show the modal with the target account
          setTargetAccountId(newAccountId);
          setIsStale(true);
        }
      }
    };
    window.addEventListener("storage", showAlert);

    return () => window.removeEventListener("storage", showAlert);
  }, [userId, currentAccount?.ID]);

  return (
    <Modal
      open={isStale}
      header="Workspace switched in another tab"
      content={
        currentAccount && targetAccount ? (
          <AccountSwitchDisplay
            currentAccount={currentAccount}
            targetAccount={targetAccount}
            warningMessage="The workspace selected has been switched in a different tab. You can switch to the new workspace or stay on the current workspace."
          />
        ) : null
      }
      actions={
        <>
          <Button
            data-testid="stay-on-account-button"
            variant="outlined"
            onClick={() => {
              // Revert localStorage to match the current Redux account context
              if (userId && currentAccount?.ID) {
                setAccountContext(userId, currentAccount.ID);
              }
              setIsStale(false);
            }}
          >
            Stay on this workspace
          </Button>
          <Button
            data-testid="refresh-button"
            variant="contained"
            color="primary"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Switch to new workspace
          </Button>
        </>
      }
    />
  );
};
